/**
 * Document-flow activity timeline (W4 — Scrive lifecycle visibility).
 *
 * Renders the full Scrive document process as a vertical timeline:
 *
 *   • Skickat för signering (sent)
 *   • Öppnat av <party> (viewed, per party)
 *   • Signerat av <party> (signed, per party)
 *   • Alla parter har signerat — digitalt signerat (closed, 1st callback)
 *   • Långtidsförseglat (sealed_at, keyless seal — 2nd closed callback)
 *   • Avbrutet / Avvisat / Tid utgick (cancelled / rejected / timedout)
 *
 * Backed by `signing.activity` which joins audit_logs + the contract's
 * own scrive_status / signed_at / sealed_at columns + per-party
 * seen_at / signed_at. Per ADR-0019, no PII leaves the backend — the
 * audit payload is `sanitize()`-branded.
 *
 * Polls every 5s while the contract is non-terminal so the timeline
 * stays live without a webhook round-trip to the client.
 */

import { useMemo } from 'react';
import {
  tenantApi,
  type ContractDetail,
  type ScriveActivityEvent,
  type ScriveActivityParty,
  type ScriveActivityResponse,
} from '../api.js';
import { useTenantQuery } from '../query.js';

// Terminal statuses where polling adds no value. `signed_external` is
// the imported-paper-contract status (out of Scrive flow entirely).
const TERMINAL_STATUSES = new Set<string>(['signed', 'cancelled', 'signed_external']);

export function ScriveActivityTimeline({
  token,
  contract,
}: {
  token: string;
  contract: ContractDetail;
}) {
  const activityQ = useTenantQuery(
    'signing.activity',
    (cid) => tenantApi.signing.activity({ contract_id: cid as string }),
    [contract.id],
    {
      ignoreActiveGroup: true,
      refreshInterval: TERMINAL_STATUSES.has(contract.status) ? 0 : 5000,
      revalidateOnFocus: false,
    },
  );

  const activity = activityQ.data;
  const items = useMemo(
    () => (activity ? buildTimeline(activity) : []),
    [activity],
  );

  // Hide on contracts that have never been sent — there's nothing
  // meaningful to render and the "Created" line on its own is noise.
  if (
    contract.status === 'draft' ||
    contract.status === 'pending' ||
    contract.status === 'in_review'
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="border-b border-stone-200 pb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
        Aktivitet
      </div>
      {items.length === 0 ? (
        <div className="rounded border border-stone-200 bg-stone-50 p-3 text-xs text-stone-500">
          Inga händelser att visa än.
        </div>
      ) : (
        <ol className="flex flex-col gap-0">
          {items.map((it, idx) => (
            <TimelineRow key={it.key} entry={it} last={idx === items.length - 1} />
          ))}
        </ol>
      )}
      {activity?.contract.sealed_at && (
        <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900">
          Långtidsförseglat {formatTs(activity.contract.sealed_at)} — Scrive har applicerat
          den nyckellösa förseglingen som långtidsbevis på dokumentets integritet.
        </div>
      )}
    </div>
  );
}

// ─── Timeline construction ───────────────────────────────────────────

type TimelineEntry = {
  key: string;
  occurredAt: string;
  icon: 'send' | 'view' | 'sign' | 'completed' | 'sealed' | 'cancelled' | 'info';
  primary: string;
  secondary?: string;
};

function buildTimeline(act: ScriveActivityResponse): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  const partyByEmail = new Map<string, ScriveActivityParty>();
  for (const p of act.parties) {
    if (p.email) partyByEmail.set(p.email.toLowerCase(), p);
  }

  // Audit events seed most of the timeline. We promote them to
  // user-facing labels here so a future audit-action rename doesn't
  // ripple to the UI.
  for (const ev of act.timeline) {
    const e = mapEvent(ev);
    if (e) entries.push(e);
  }

  // Per-party seen / signed timestamps — additive to the audit events
  // (audit emits per status flip, not per party). De-dupe against any
  // identical second-precision audit entries.
  for (const p of act.parties) {
    if (p.seen_at) {
      entries.push({
        key: `seen:${p.id}`,
        occurredAt: p.seen_at,
        icon: 'view',
        primary: `${p.legal_name} öppnade dokumentet`,
        secondary: roleLabel(p.role),
      });
    }
    if (p.signed_at) {
      entries.push({
        key: `signed:${p.id}`,
        occurredAt: p.signed_at,
        icon: 'sign',
        primary: `${p.legal_name} signerade`,
        secondary: roleLabel(p.role),
      });
    }
  }

  // Sort chronologically. Stable on identical timestamps so the
  // per-party rows stay near their corresponding audit events.
  entries.sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  // Render-time labels include relative ts only; keep raw for tooltips.
  return entries;
}

function mapEvent(ev: ScriveActivityEvent): TimelineEntry | null {
  const payload = ev.payload as { scrive_document_id?: string };
  switch (ev.action) {
    case 'contract.send.requested':
      return null; // audit-only; the `contract.sent` event below is what users care about
    case 'contract.scrive.created':
      return null; // internal step in the send flow
    case 'contract.sent':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'send',
        primary: 'Skickat för signering',
        secondary: payload.scrive_document_id
          ? `Scrive-id: ${payload.scrive_document_id.slice(-8)}`
          : undefined,
      };
    case 'signing.scrive.viewed':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'view',
        primary: 'Dokumentet öppnades',
      };
    case 'signing.scrive.declined':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'cancelled',
        primary: 'En part avvisade signering',
      };
    case 'signing.scrive.expired':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'cancelled',
        primary: 'Tiden för signering gick ut',
      };
    case 'signing.scrive.sealed':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'sealed',
        primary: 'Långtidsförseglat',
        secondary: 'Scrive applicerade den nyckellösa förseglingen',
      };
    case 'contract.signing.cancelled':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'cancelled',
        primary: 'Signering avbruten',
      };
    case 'contract.signing.returned_to_draft':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'cancelled',
        primary: 'Återgick till utkast',
      };
    case 'contract.completed':
      return {
        key: ev.id,
        occurredAt: ev.occurred_at,
        icon: 'completed',
        primary: 'Alla parter har signerat — digitalt signerat',
        secondary: 'Slutgiltig PDF tillgänglig',
      };
    default:
      return null;
  }
}

function TimelineRow({ entry, last }: { entry: TimelineEntry; last: boolean }) {
  return (
    <li className="relative grid grid-cols-[20px_1fr] gap-2 pb-3 last:pb-0">
      {/* Vertical connector */}
      {!last && (
        <span
          aria-hidden="true"
          className="absolute left-[9px] top-4 h-full w-px bg-stone-200"
        />
      )}
      <span
        className={`relative z-10 mt-1 inline-flex h-[18px] w-[18px] items-center justify-center rounded-full ${iconBg(
          entry.icon,
        )}`}
      >
        <Glyph icon={entry.icon} />
      </span>
      <div className="min-w-0">
        <div className="text-xs font-medium text-stone-800">{entry.primary}</div>
        <div className="flex items-baseline gap-2 text-[10px] text-stone-500">
          <time dateTime={entry.occurredAt} title={entry.occurredAt}>
            {formatTs(entry.occurredAt)}
          </time>
          {entry.secondary && <span>· {entry.secondary}</span>}
        </div>
      </div>
    </li>
  );
}

function iconBg(icon: TimelineEntry['icon']): string {
  switch (icon) {
    case 'send':
      return 'bg-blue-100 text-blue-700';
    case 'view':
      return 'bg-stone-100 text-stone-600';
    case 'sign':
      return 'bg-emerald-100 text-emerald-700';
    case 'completed':
      return 'bg-emerald-200 text-emerald-800';
    case 'sealed':
      return 'bg-purple-100 text-purple-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-stone-100 text-stone-600';
  }
}

function Glyph({ icon }: { icon: TimelineEntry['icon'] }) {
  const ch =
    icon === 'send'
      ? '→'
      : icon === 'view'
        ? '◌'
        : icon === 'sign'
          ? '✓'
          : icon === 'completed'
            ? '✓'
            : icon === 'sealed'
              ? '✦'
              : icon === 'cancelled'
                ? '×'
                : '·';
  return <span className="text-[11px] leading-none">{ch}</span>;
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('sv-SE')} ${d.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  } catch {
    return iso;
  }
}

function roleLabel(role: string): string {
  switch (role) {
    case 'lessor':
      return 'Hyresvärd';
    case 'lessee':
      return 'Hyresgäst';
    case 'guarantor':
      return 'Borgensman';
    case 'witness':
      return 'Vittne';
    default:
      return role;
  }
}
